/**
 * The canonical "what gets deleted" list — single source of truth for the
 * confirm modal and the post-deletion landing.
 *
 * Order is intentional: subscription first (least personal), the parent
 * account last (most final). Each maps 1:1 to a deletion target the
 * backend clears.
 */
import type { IconName } from "../chrome/Icon";

export interface ErasureCategory {
  icon: IconName;
  label: string;
  detail: string;
}

export interface FamilySummary {
  /** Plan label, e.g. "Family". */
  plan?: string | null;
  /** Active kid display names. */
  kidNames: string[];
}

export function erasureCategories(family: FamilySummary): ErasureCategory[] {
  const plan = family.plan || "Family";
  const kids = family.kidNames;
  const kidLabel = kids.length === 0
    ? "Your child profiles"
    : kids.length === 1
      ? `1 child profile — ${kids[0]}`
      : `${kids.length} child profiles — ${kids.join(" & ")}`;
  return [
    {
      icon: "credit-card",
      label: `Your ${plan} subscription`,
      detail: "Billing stops now — you won't be charged again.",
    },
    {
      icon: "users",
      label: kidLabel,
      detail: "Names, ages, settings and saved looks.",
    },
    {
      icon: "layout-grid",
      label: "Every project they made — worlds, games & stories",
      detail: "Including anything still in progress.",
    },
    {
      icon: "message-circle",
      label: "Every conversation with the voice co-explorer",
      detail: "Recordings and transcripts.",
    },
    {
      icon: "smile",
      label: "Every avatar they designed",
      detail: "Every face, outfit and saved look.",
    },
    {
      icon: "share-2",
      label: "Every shared world & friend connection",
      detail: "Visits, hearts and invitations.",
    },
    {
      icon: "user",
      label: "Your parent account & sign-in",
      detail: "This is the last thing to go.",
    },
  ];
}
